import { z } from "zod";

export const LIMITS = {
  maxCharacters: 5, maxRefsPerCharacter: 8, maxSceneRefs: 6, maxTotalRefs: 40, urlMax: 2048,
  name: 60, blockText: 1000, sceneDescription: 300, notes: 1000, clientName: 100, discord: 50, commissionType: 120,
} as const;

export const USAGE_TYPES = ["personal", "commercial"] as const;
export const CONTENT_RATINGS = ["sfw", "suggestive", "nsfw"] as const;
export const POSTING_PERMISSIONS = ["yes", "delayed", "no"] as const;
export const BACKGROUND_TYPES = ["none", "simple", "complex"] as const;

/** Same pattern as the DB constraint clients_email_format, so anything we accept can be saved. */
export const CLIENT_EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

function isRealDate(iso: string): boolean {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

const optText = (max: number) =>
  z.string().trim().max(max).optional().transform((v) => (v ? v : undefined));

export const RefUrlSchema = z
  .string()
  .max(LIMITS.urlMax)
  .refine((u) => u.startsWith("https://res.cloudinary.com/"), { message: "ref_not_cloudinary" });

/** One open question on a character: any mix of images and free text. */
export const BlockSchema = z.object({
  text: optText(LIMITS.blockText),
  refs: z.array(RefUrlSchema).max(LIMITS.maxRefsPerCharacter),
});

const blockRefs = (c: { look: { refs: string[] }; action: { refs: string[] }; extra: { refs: string[] } }) =>
  [...c.look.refs, ...c.action.refs, ...c.extra.refs];

export const CharacterSchema = z
  .object({
    type: z.string().trim().min(1).max(LIMITS.commissionType),
    name: optText(LIMITS.name),
    look: BlockSchema,
    action: BlockSchema,
    extra: BlockSchema,
  })
  // Guide, never limit: pictures OR words are enough, but the artist needs something.
  .refine((c) => blockRefs(c).length > 0 || [c.look.text, c.action.text, c.extra.text].some(Boolean), {
    message: "character_empty",
  })
  .refine((c) => blockRefs(c).length <= LIMITS.maxRefsPerCharacter, { message: "too_many_refs" });

export const IntakeSchema = z
  .object({
    schema_version: z.literal(2),
    characters: z.array(CharacterSchema).min(1).max(LIMITS.maxCharacters),
    scene: z
      .object({ description: optText(LIMITS.sceneDescription), refs: z.array(RefUrlSchema).max(LIMITS.maxSceneRefs) })
      .optional(),
    notes: optText(LIMITS.notes),
    selected_fees: z.array(z.string().max(60)).max(20),
    locale: z.enum(["en", "pt-BR"]),
  })
  .refine(
    (i) => i.characters.reduce((n, c) => n + blockRefs(c).length, 0) + (i.scene?.refs.length ?? 0) <= LIMITS.maxTotalRefs,
    { message: "too_many_refs", path: ["characters"] },
  );

export const SubmitPayloadSchema = z
  .object({
    artist_id: z.string().uuid(),
    character_count: z.number().int().min(1).max(LIMITS.maxCharacters),
    background_type: z.enum(BACKGROUND_TYPES),
    usage_type: z.enum(USAGE_TYPES),
    content_rating: z.enum(CONTENT_RATINGS),
    posting_permission: z.enum(POSTING_PERMISSIONS),
    deadline: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(isRealDate, { message: "invalid_date" }).nullable(),
    client: z.object({
      name: z.string().trim().min(1).max(LIMITS.clientName),
      email: z.string().trim().toLowerCase().max(254).regex(CLIENT_EMAIL_RE, { message: "invalid_email" }),
      discord: z.string().trim().max(LIMITS.discord).optional(),
    }),
    studio_tos_hash: z.string().regex(/^[a-f0-9]{64}$/),
    artist_tos_hash: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
    intake: IntakeSchema,
    website: z.string().optional(), // honeypot, checked before parsing
  })
  .superRefine((p, ctx) => {
    if (p.intake.characters.length !== p.character_count) {
      ctx.addIssue({ code: "custom", path: ["intake", "characters"], message: "character_count_mismatch" });
    }
    if (p.intake.scene && p.background_type !== "complex") {
      ctx.addIssue({ code: "custom", path: ["intake", "scene"], message: "scene_requires_complex" });
    }
  });

export type IntakeCharacter = z.infer<typeof CharacterSchema>;
export type Intake = z.infer<typeof IntakeSchema>;
export type SubmitPayload = z.infer<typeof SubmitPayloadSchema>;

export function allRefs(intake: Intake): string[] {
  return [...intake.characters.flatMap(blockRefs), ...(intake.scene?.refs ?? [])];
}
